import { Theme } from '@mui/material/styles';
import { TFunction } from 'i18next';

import type { TMplChipVariant } from '@mopla/ui';
import { EItineraryTimeChange, ERideStatus } from '@mopla/utils';

export interface IRideStatusChipConfig {
	label: string | null;
	variant: TMplChipVariant;
	/** Overrides the variant background for statuses that need a custom color. */
	bgColor?: string;
	/** Overrides the variant text color for statuses that need a custom color. */
	textColor?: string;
}

/**
 * Statuses the "Verspätet"/"Früher" chip may replace. Higher-priority
 * statuses (on the way, arrived, in ride, completed, cancelled) always win
 * over the time-change chip.
 */
const TIME_CHANGE_OVERRIDABLE_STATUSES: ReadonlyArray<ERideStatus | null> = [
	ERideStatus.BOOKED,
	ERideStatus.SCHEDULED,
	null,
];

export const getRideStatusChipConfig = (
	status: ERideStatus | null,
	timeChange: EItineraryTimeChange,
	t: TFunction,
	theme: Theme
): IRideStatusChipConfig => {
	if (TIME_CHANGE_OVERRIDABLE_STATUSES.includes(status)) {
		if (
			timeChange === EItineraryTimeChange.DELAY ||
			timeChange === EItineraryTimeChange.INDETERMINATE_DELAY
		) {
			return {
				label: t('booking:booked.detail.ride_status.delayed'),
				variant: 'warning',
			};
		}
		if (timeChange === EItineraryTimeChange.EARLIER) {
			return {
				label: t('booking:booked.detail.ride_status.earlier'),
				variant: 'warning',
			};
		}
	}

	switch (status) {
		case ERideStatus.BOOKED:
			return {
				label: t('booking:booked.detail.ride_status.booked'),
				variant: 'secondary',
			};
		case ERideStatus.SCHEDULED:
			return {
				label: t('booking:booked.detail.ride_status.scheduled'),
				variant: 'success',
			};
		case ERideStatus.ON_THE_WAY:
		case ERideStatus.IN_RIDE:
			return {
				label: t('booking:booked.detail.ride_status.on_the_way'),
				variant: 'primary',
				bgColor: theme.palette.myway?.main,
			};
		case ERideStatus.ARRIVED:
			return {
				label: t('booking:booked.detail.ride_status.arrived'),
				variant: 'primary',
				bgColor: theme.palette.myway?.main,
			};
		case ERideStatus.CHECKED_IN:
			return {
				label: t('booking:booked.detail.ride_status.checked_in'),
				variant: 'success',
			};
		case ERideStatus.COMPLETED:
			return {
				label: t('booking:booked.detail.ride_status.completed'),
				variant: 'info',
			};
		case ERideStatus.CANCELLED:
			return {
				label: t('booking:booked.detail.ride_status.cancelled'),
				variant: 'error',
			};
		default:
			return { label: null, variant: 'default' };
	}
};
